<template>
  <div
    v-if="modalProps"
    class="modal-wrapper"
    :class="modalProps.IsDismissable ? 'dismissable' : ''"
  >
    <div v-if="modalProps.IsDismissable" class="sheet-modal-close-button">
      <div class="flex flex-row justify-center" @click="dismissModal">
        <div class="modal-close-button color-text bg-light-translucent cursor-pointer">
          <IonIcon :icon="close" class="modal-close-icon"></IonIcon>
        </div>
      </div>
    </div>
    <div class="modal-content-wrapper flex flex-col bg-color-light-light">
      <div
        class="row-center modal-header justify-between user-select-none"
        :class="`bg-color-${modalProps.ModalColor}-dark`"
      >
        <div
          v-if="modalProps.ModalTitle"
          class="modal-title text-s font-semibold color-light truncate px-3 py-2"
        >
          {{ modalProps.ModalTitle }}
        </div>
        <div v-else></div>
        <i
          v-if="modalProps.IsDismissable"
          class="inline-modal-close-button ti ti-x text-m leading-xs color-light px-3 py-2 cursor-pointer"
          @click="dismissModal"
        ></i>
      </div>
      <div class="modal-content">
        <slot name="contentSlot"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import { IonModalProps } from "@/types/global";
import { IonContent, IonIcon } from "@ionic/vue";
import { close } from "ionicons/icons";
import { storeToRefs } from "pinia";
import { defineComponent, PropType } from "vue";

export default defineComponent({
  name: "CustomIonModalContent",
  components: { IonContent, IonIcon },
  props: { modalProps: { type: Object as PropType<IonModalProps>, required: true } },
  setup() {
    const appSettingsStore = useAppSettingsStore();
    const { IsTabletScreen } = storeToRefs(appSettingsStore);
    const ionModalStore = useIonModalStore();
    const { dismissModal } = ionModalStore;
    return { close, IsTabletScreen, dismissModal };
  },
});
</script>

<style lang="scss">
.modal-wrapper {
  background-color: transparent;
  & .sheet-modal-close-button {
    display: none;
  }
  & .modal-content-wrapper {
    background-color: var(--color-light-light);
    & .inline-modal-close-button {
      display: none;
    }
    & .modal-content {
      overflow-y: auto;
      // Clip sub-pixel horizontal overflow (e.g. the iOS toggle handle) — a fixed-width modal never
      // scrolls sideways. Without this, overflow-y: auto promotes overflow-x (initial `visible`) to
      // `auto`, so any 1px overrun renders a bottom scrollbar. Mirrors AdminAffiliationFormView.
      overflow-x: hidden;
    }
  }
}

ion-modal.custom-default-modal,
ion-modal.custom-sheet-modal {
  & .modal-wrapper {
    --close-icon-size: 0;
    --close-icon-box-size: 0;
    --close-icon-margin: 0;
    --close-icon-total-size: 0;
    &.dismissable {
      --close-icon-size: 1.33rem;
      --close-icon-box-size: 2rem;
      --close-icon-margin: 0.33rem;
      --close-icon-total-size: calc(
        var(--close-icon-box-size) + 2 * var(--close-icon-margin) - 10px
      );
    }
    & .sheet-modal-close-button {
      display: block;
      & .modal-close-button {
        position: relative;
        width: var(--close-icon-box-size);
        height: var(--close-icon-box-size);
        margin: var(--close-icon-margin);
        border-radius: 50%;
        & .modal-close-icon {
          position: absolute;
          font-size: var(--close-icon-size);
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }
  }
}

ion-modal.custom-default-modal {
  --background: transparent;
  --height: auto;
  @include media("<=s") {
    --width: 95%;
  }
  & .modal-wrapper {
    & .modal-header {
      border-radius: var(--control-radius) var(--control-radius) 0 0 !important;
    }
    & .modal-content-wrapper {
      border-radius: var(--control-radius) !important;
      max-height: calc(100vh - var(--close-icon-total-size));
      max-height: calc(100svh - var(--close-icon-total-size));
    }
  }
}

ion-modal.custom-inline-modal {
  // Mobile-first: full-width by default, inset to 80% on l+ viewports.
  --width: 100%;
  --height: 100%;
  --border-radius: 0;
  @include media(">=l") {
    --width: 80%;
  }
  & .modal-wrapper {
    height: 100%;
    & .modal-content-wrapper {
      height: 100%;
      border-radius: 0 !important;
      & .modal-header {
        padding-top: var(--ion-safe-area-top);
      }
      & .modal-content {
        height: 100%;
        padding-bottom: var(--ion-safe-area-bottom);
        padding-left: var(--ion-safe-area-left);
        padding-right: var(--ion-safe-area-right);
      }
      & .inline-modal-close-button {
        display: block;
      }
    }
  }
}

ion-modal.custom-sheet-modal {
  --background: transparent;
  --height: auto;
  @include media("<=s") {
    --width: 95%;
  }
  & .modal-wrapper {
    & .modal-content-wrapper {
      max-height: calc(100vh - calc(var(--close-icon-total-size) + var(--ion-safe-area-top) + 5px));
      max-height: calc(
        100svh - calc(var(--close-icon-total-size) + var(--ion-safe-area-top) + 5px)
      );
      border-radius: 0.5rem;
      overflow: hidden;
      & .modal-content {
        padding-bottom: var(--ion-safe-area-bottom);
        padding-left: var(--ion-safe-area-left);
        padding-right: var(--ion-safe-area-right);
      }
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
}
</style>
